<script setup>
import { ref, computed, provide, onMounted, markRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { NODE_CONFIG } from '../constants/nodeTypes.js';
import { extractJsonPaths } from '../constants/flowVariables.js';
import { useRoute, useRouter } from 'vue-router';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import { useFlowBuilderStore } from '../store/index.js';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useAlert } from 'dashboard/composables';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import NodeConfigPanel from '../components/panels/NodeConfigPanel.vue';
import NodePickerModal from '../components/NodePickerModal.vue';
import TriggerNode from '../components/nodes/TriggerNode.vue';
import ActionNode from '../components/nodes/ActionNode.vue';
import ConditionNode from '../components/nodes/ConditionNode.vue';
import DelayNode from '../components/nodes/DelayNode.vue';
import WaitBusinessHoursNode from '../components/nodes/WaitBusinessHoursNode.vue';
import SmartDelayNode from '../components/nodes/SmartDelayNode.vue';
import AiConditionNode from '../components/nodes/AiConditionNode.vue';
import GenerateMessageNode from '../components/nodes/GenerateMessageNode.vue';
import SummarizeNode from '../components/nodes/SummarizeNode.vue';
import EndNode from '../components/nodes/EndNode.vue';
import CaptainNode from '../components/nodes/CaptainNode.vue';
import ClassifyNode from '../components/nodes/ClassifyNode.vue';
import ExtractDataNode from '../components/nodes/ExtractDataNode.vue';
import RecommendProductNode from '../components/nodes/RecommendProductNode.vue';
import WaitReplyNode from '../components/nodes/WaitReplyNode.vue';
import SetVariablesNode from '../components/nodes/SetVariablesNode.vue';
import QueryDatabaseNode from '../components/nodes/QueryDatabaseNode.vue';
import ForEachNode from '../components/nodes/ForEachNode.vue';
import HttpRequestNode from '../components/nodes/HttpRequestNode.vue';
import SetContextNode from '../components/nodes/SetContextNode.vue';
import FilterArrayNode from '../components/nodes/FilterArrayNode.vue';
import SwitchNode from '../components/nodes/SwitchNode.vue';
import MergeNode from '../components/nodes/MergeNode.vue';
import WaitExternalEventNode from '../components/nodes/WaitExternalEventNode.vue';
import CallFlowNode from '../components/nodes/CallFlowNode.vue';
import DatatableNode from '../components/nodes/DatatableNode.vue';
import CodeNode from '../components/nodes/CodeNode.vue';
import FlowEdge from '../components/edges/FlowEdge.vue';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useFlowBuilderStore();
const { accountScopedRoute } = useAccount();

const nodeTypes = markRaw({
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  delay: DelayNode,
  wait_business_hours: WaitBusinessHoursNode,
  smart_delay: SmartDelayNode,
  ai_condition: AiConditionNode,
  generate_message: GenerateMessageNode,
  summarize: SummarizeNode,
  captain: CaptainNode,
  classify: ClassifyNode,
  extract_data: ExtractDataNode,
  recommend_product: RecommendProductNode,
  set_variables: SetVariablesNode,
  query_database: QueryDatabaseNode,
  for_each: ForEachNode,
  http_request: HttpRequestNode,
  set_context: SetContextNode,
  filter_array: FilterArrayNode,
  switch: SwitchNode,
  merge: MergeNode,
  wait_external_event: WaitExternalEventNode,
  call_flow: CallFlowNode,
  wait_reply: WaitReplyNode,
  datatable_query: DatatableNode,
  datatable_insert: DatatableNode,
  datatable_update: DatatableNode,
  datatable_delete: DatatableNode,
  code: CodeNode,
  end: EndNode,
});

const edgeTypes = markRaw({ flow: FlowEdge });

const {
  onConnect,
  screenToFlowCoordinate,
  project,
  vueFlowRef,
  findNode,
  applyNodeChanges,
  applyEdgeChanges,
  toObject,
} = useVueFlow();

const nodes = ref([]);
const edges = ref([]);
const selectedNode = ref(null);
const flowName = ref('');
const flowSettings = ref({});

const SETTINGS_SENTINEL = {
  type: '__flow_settings__',
  id: '__flow_settings__',
  data: {},
};
const contextMenu = ref(null);
const isEditingName = ref(false);
const editingName = ref('');

function startEditingName() {
  editingName.value = flowName.value;
  isEditingName.value = true;
}

async function commitName() {
  const trimmed = editingName.value.trim();
  isEditingName.value = false;
  if (!trimmed || trimmed === flowName.value) return;
  flowName.value = trimmed;
  await onSave();
}

const canvasRef = ref(null);
const picker = ref({
  open: false,
  edgeId: null,
  source: null,
  sourceHandle: null,
  target: null,
  dropPosition: null,
});

function openNodePicker({ edgeId, source, target }) {
  const originalEdge = edges.value.find(e => e.id === edgeId);
  picker.value = {
    open: true,
    edgeId,
    source,
    sourceHandle: originalEdge?.sourceHandle ?? null,
    target,
    dropPosition: null,
  };
}

let pendingConn = null;
let connectionMade = false;

function onConnectStart(payload) {
  const nodeId = payload?.nodeId ?? payload?.node?.id ?? null;
  const handleId = payload?.handleId ?? payload?.handle?.id ?? null;
  pendingConn = { source: nodeId, sourceHandle: handleId ?? null };
  connectionMade = false;
}

function onConnectEnd(event) {
  if (!pendingConn) return;
  const { source, sourceHandle } = pendingConn;
  pendingConn = null;
  if (connectionMade) {
    connectionMade = false;
    return;
  }

  const dropPosition = screenToFlowCoordinate({
    x: event.clientX,
    y: event.clientY,
  });
  // setTimeout 0: lets the browser fire the pending click event (which would close the dialog)
  // before we open the picker, so OnClickOutside doesn't immediately dismiss it.
  setTimeout(() => {
    picker.value = {
      open: true,
      edgeId: null,
      source,
      sourceHandle,
      target: null,
      dropPosition,
    };
  }, 0);
}

provide('openNodePicker', openNodePicker);

const flowTriggerEvent = computed(() => {
  const triggerNode = nodes.value.find(n => n.type === 'trigger');
  return triggerNode?.data?.event ?? null;
});
provide('flowTriggerEvent', flowTriggerEvent);

function columnsForVariable(varName, allNodes) {
  const source = allNodes.find(
    n =>
      ['query_database', 'datatable_query'].includes(n.type) &&
      n.data.variable_name?.trim() === varName
  );
  if (!source) return [];
  return source.type === 'query_database'
    ? (source.data.columns ?? [])
    : (source.data.table_columns ?? []);
}

function extractNodeVariables(node, allNodes = []) {
  const result = [];
  if (node.type === 'set_variables') {
    (node.data.variables ?? []).forEach(v => {
      if (v.key?.trim())
        result.push({ key: `variables.${v.key.trim()}`, label: v.key.trim() });
    });
  }
  if (node.type === 'query_database' && node.data.variable_name?.trim()) {
    const vn = node.data.variable_name.trim();
    result.push({ key: `variables.${vn}`, label: vn });
    result.push({ key: `variables.${vn}_count`, label: `${vn}_count` });
  }
  if (node.type === 'datatable_query' && node.data.variable_name?.trim()) {
    const vn = node.data.variable_name.trim();
    result.push({ key: `variables.${vn}`, label: vn });
    result.push({ key: `variables.${vn}_count`, label: `${vn}_count` });
  }
  if (node.type === 'datatable_insert' && node.data.variable_name?.trim()) {
    const vn = node.data.variable_name.trim();
    result.push({ key: `variables.${vn}`, label: vn });
  }
  if (node.type === 'for_each') {
    const itemVar = node.data.item_variable?.trim() || 'item';
    const indexVar = node.data.item_index_variable?.trim() || 'item_index';
    result.push({ key: `variables.${itemVar}`, label: itemVar });
    result.push({ key: `variables.${indexVar}`, label: indexVar });
    const cols = columnsForVariable(node.data.variable_name, allNodes);
    cols.forEach(col => {
      result.push({
        key: `variables.${itemVar}.${col}`,
        label: `${itemVar}.${col}`,
      });
    });
  }
  if (node.type === 'http_request' && node.data.variable_name?.trim()) {
    const vn = node.data.variable_name.trim();
    result.push({ key: `variables.${vn}`, label: vn });
    if (node.data.sample_response) {
      try {
        const parsed = JSON.parse(node.data.sample_response);
        extractJsonPaths(parsed).forEach(path => {
          result.push({
            key: `variables.${vn}.${path}`,
            label: `${vn}.${path}`,
          });
        });
      } catch {}
    }
  }
  if (node.type === 'code' && node.data.variable_name?.trim()) {
    const vn = node.data.variable_name.trim();
    result.push({ key: `variables.${vn}`, label: vn });
  }
  return result;
}

function getAncestorIds(nodeId) {
  const visited = new Set();
  const queue = [nodeId];
  while (queue.length > 0) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    edges.value.filter(e => e.target === id).forEach(e => queue.push(e.source));
  }
  visited.delete(nodeId);
  return visited;
}

function getUpstreamVariables(nodeId) {
  const ancestorIds = getAncestorIds(nodeId);
  const result = [];
  nodes.value
    .filter(n => ancestorIds.has(n.id))
    .forEach(n => result.push(...extractNodeVariables(n, nodes.value)));

  // event.data.* do trigger webhook (sempre visível, é o início do fluxo)
  const triggerNode = nodes.value.find(n => n.type === 'trigger');
  if (
    triggerNode?.data?.event === 'webhook_received' &&
    triggerNode?.data?.sample_payload
  ) {
    try {
      const parsed = JSON.parse(triggerNode.data.sample_payload);
      extractJsonPaths(parsed).forEach(path => {
        result.push({ key: `event.data.${path}`, label: path });
      });
    } catch {}
  }
  return result;
}

const flowDynamicVariables = computed(() => {
  const result = [];
  nodes.value.forEach(node =>
    result.push(...extractNodeVariables(node, nodes.value))
  );

  // event.data.* do sample_payload do trigger webhook
  const triggerNode = nodes.value.find(n => n.type === 'trigger');
  if (
    triggerNode?.data?.event === 'webhook_received' &&
    triggerNode?.data?.sample_payload
  ) {
    try {
      const parsed = JSON.parse(triggerNode.data.sample_payload);
      extractJsonPaths(parsed).forEach(path => {
        result.push({ key: `event.data.${path}`, label: path });
      });
    } catch {}
  }

  return result;
});
provide('flowDynamicVariables', flowDynamicVariables);
provide('getUpstreamVariables', getUpstreamVariables);

const nodeAudience = computed(() => store.nodeAudience);
provide('nodeAudience', nodeAudience);

provide('flowSettings', flowSettings);
provide('updateFlowSettings', patch => {
  flowSettings.value = { ...flowSettings.value, ...patch };
});
const flowList = computed(() => store.flows);
provide('flowList', flowList);
provide(
  'flowWebhookToken',
  computed(() => store.currentFlow?.webhook_token ?? '')
);
provide(
  'flowWebhookSecret',
  computed(() => store.currentFlow?.webhook_secret ?? '')
);

function deleteEdge(edgeId) {
  applyEdgeChanges([{ type: 'remove', id: edgeId }]);
}

provide('deleteEdge', deleteEdge);

const NAMED_TYPES = [
  'action',
  'condition',
  'delay',
  'wait_business_hours',
  'smart_delay',
  'wait_reply',
  'ai_condition',
  'generate_message',
  'summarize',
  'captain',
  'classify',
  'extract_data',
  'set_variables',
];

function getNodeName(type) {
  const base = NODE_CONFIG[type]?.label ? t(NODE_CONFIG[type].label) : type;
  const used = nodes.value
    .filter(n => n.type === type)
    .map(n => {
      const m = n.data?.name?.match(new RegExp(`^${base} (\\d+)$`));
      return m ? parseInt(m[1]) : null;
    })
    .filter(n => n !== null);
  let i = 1;
  while (used.includes(i)) i++;
  return `${base} ${i}`;
}

function onPickNode(type, data = {}) {
  const { edgeId, source, sourceHandle, target, dropPosition } = picker.value;
  picker.value = {
    open: false,
    edgeId: null,
    source: null,
    sourceHandle: null,
    target: null,
    dropPosition: null,
  };

  const ts = Date.now();
  const newId = `${type}-${ts}`;
  const name = NAMED_TYPES.includes(type) ? getNodeName(type) : undefined;
  const defaultData =
    type === 'condition'
      ? {
          conditions: [
            {
              attribute_key: 'status',
              filter_operator: 'equal_to',
              values: '',
              query_operator: null,
            },
          ],
        }
      : type === 'delay'
        ? { delay_minutes: 1 }
        : {};
  const nodeData = { ...defaultData, ...data, ...(name ? { name } : {}) };

  // Drop on canvas — create node at drop position and connect from source
  if (!target) {
    if (!source || !dropPosition) return;
    nodes.value = [
      ...nodes.value,
      { id: newId, type, position: dropPosition, data: nodeData },
    ];
    edges.value = [
      ...edges.value,
      {
        id: `e-${ts}`,
        source,
        target: newId,
        type: 'flow',
        ...(sourceHandle ? { sourceHandle } : {}),
      },
    ];
    return;
  }

  // Insert between existing edge
  const sourceNode = findNode(source);
  const targetNode = findNode(target);
  if (!sourceNode || !targetNode) return;

  const midX = (sourceNode.position.x + targetNode.position.x) / 2;
  const midY = (sourceNode.position.y + targetNode.position.y) / 2;

  const originalEdge = edges.value.find(e => e.id === edgeId);
  const inheritedSourceHandle = originalEdge?.sourceHandle;

  nodes.value = [
    ...nodes.value,
    { id: newId, type, position: { x: midX, y: midY }, data: nodeData },
  ];
  edges.value = [
    ...edges.value.filter(e => e.id !== edgeId),
    {
      id: `e-${ts}-1`,
      source,
      target: newId,
      type: 'flow',
      ...(inheritedSourceHandle ? { sourceHandle: inheritedSourceHandle } : {}),
    },
    {
      id: `e-${ts + 1}-2`,
      source: newId,
      target,
      type: 'flow',
      ...(NODE_CONFIG[type]?.defaultSourceHandle
        ? { sourceHandle: NODE_CONFIG[type].defaultSourceHandle }
        : {}),
    },
  ];
}

onMounted(async () => {
  await store.fetchFlow(route.params.flowId);
  if (store.currentFlow) {
    flowName.value = store.currentFlow.name;
    nodes.value = store.currentFlow.flow_data?.nodes ?? [];
    edges.value = (store.currentFlow.flow_data?.edges ?? []).map(e => ({
      ...e,
      type: e.type ?? 'flow',
    }));
    flowSettings.value = store.currentFlow.flow_data?.settings ?? {};
  }
  store.fetchNodeAudience(route.params.flowId);
  store.fetchFlows();
});

onConnect(params => {
  connectionMade = true;
  edges.value = [
    ...edges.value,
    { ...params, id: `e-${Date.now()}`, type: 'flow' },
  ];
});

function onNodeClick({ node }) {
  selectedNode.value = node;
  contextMenu.value = null;
}

function onNodeContextMenu({ node, event }) {
  event.preventDefault();
  contextMenu.value = { x: event.clientX, y: event.clientY, nodeId: node.id };
}

function deleteNode(nodeId) {
  applyNodeChanges([{ type: 'remove', id: nodeId }]);
  applyEdgeChanges(
    edges.value
      .filter(e => e.source === nodeId || e.target === nodeId)
      .map(e => ({ type: 'remove', id: e.id }))
  );
  if (selectedNode.value?.id === nodeId) selectedNode.value = null;
  contextMenu.value = null;
}

function onPaneClick() {
  selectedNode.value = null;
  contextMenu.value = null;
}

function onNodeDataUpdate(newData) {
  if (!selectedNode.value) return;
  nodes.value = nodes.value.map(n =>
    n.id === selectedNode.value.id ? { ...n, data: newData } : n
  );
  selectedNode.value = { ...selectedNode.value, data: newData };
}

function onDrop(event) {
  event.preventDefault();
  const type = event.dataTransfer.getData('application/vue-flow-node-type');
  if (!type) return;

  const bounds = vueFlowRef.value?.getBoundingClientRect();
  if (!bounds) return;

  const position = project({
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top,
  });

  const id = `${type}-${Date.now()}`;
  const defaultData =
    type === 'condition'
      ? {
          conditions: [
            {
              attribute_key: 'status',
              filter_operator: 'equal_to',
              values: '',
              query_operator: null,
            },
          ],
        }
      : type === 'delay'
        ? { delay_minutes: 1 }
        : {};
  const newNode = {
    id,
    type,
    position,
    data: defaultData,
  };

  nodes.value = [...nodes.value, newNode];
}

function onDragOver(event) {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
}

const isSaving = ref(false);

async function onSave() {
  isSaving.value = true;
  try {
    const { nodes: serializedNodes, edges: serializedEdges } = toObject();
    await store.saveCurrentFlow({
      name: flowName.value,
      flow_data: {
        nodes: serializedNodes,
        edges: serializedEdges,
        settings: flowSettings.value,
      },
    });
    useAlert(t('FLOW_BUILDER.EDITOR.SAVE_SUCCESS'));
  } catch {
    useAlert(t('FLOW_BUILDER.EDITOR.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

async function onToggleActive() {
  await store.toggleActive(store.currentFlow.id);
}

const isActive = computed(() => store.currentFlow?.active ?? false);

function goBack() {
  router.push(accountScopedRoute('flow_list'));
}

function goToExecutions() {
  router.push(
    accountScopedRoute('flow_executions', { flowId: route.params.flowId })
  );
}

function goToReports() {
  router.push(
    accountScopedRoute('flow_reports', { flowId: route.params.flowId })
  );
}
</script>

<template>
  <div class="flex w-full h-full overflow-hidden bg-n-background">
    <!-- Main: toolbar + canvas -->
    <div class="flex flex-col flex-1 min-w-0 overflow-hidden">
      <!-- Toolbar -->
      <div
        class="flex items-center gap-3 px-4 h-14 border-b border-n-weak bg-n-solid-1 shrink-0"
      >
        <!-- Breadcrumb: Fluxos > nome editável -->
        <nav class="flex items-center min-w-0">
          <button
            class="text-sm font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors border-0 p-0 bg-transparent"
            @click="goBack"
          >
            {{ t('FLOW_BUILDER.EDITOR.BREADCRUMB_FLOWS') }}
          </button>

          <span
            class="i-lucide-chevron-right flex-shrink-0 size-4 text-n-slate-11 mx-2"
          />

          <div
            v-if="isEditingName"
            v-on-click-outside="commitName"
            class="flex items-center"
          >
            <Input
              v-model="editingName"
              autofocus
              custom-input-class="h-8 ltr:rounded-r-none rtl:rounded-l-none w-48"
              @keyup.enter="commitName"
            />
            <Button
              sm
              icon="i-lucide-check"
              class="ltr:rounded-l-none rtl:rounded-r-none flex-shrink-0 !w-8 !h-8 !p-0"
              @click="commitName"
            />
          </div>
          <div v-else class="flex group is-editable">
            <p
              class="group-hover:bg-n-slate-3 group-hover:dark:bg-n-solid-3 inline-block rounded-sm mb-0 break-all py-0.5 px-1 text-sm font-medium text-n-slate-12 truncate max-w-[220px]"
            >
              {{ flowName }}
            </p>
            <div
              class="flex items-center max-w-[2rem] gap-1 ml-1 rtl:mr-1 rtl:ml-0"
            >
              <Button
                xs
                slate
                ghost
                icon="i-lucide-pen"
                class="hidden group-hover:flex flex-shrink-0"
                @click="startEditingName"
              />
            </div>
          </div>
        </nav>

        <div class="flex items-center gap-3 ml-auto shrink-0">
          <Button
            icon="i-lucide-settings-2"
            size="sm"
            variant="ghost"
            color="slate"
            @click="selectedNode = SETTINGS_SENTINEL"
          />
          <Button
            icon="i-lucide-activity"
            size="sm"
            variant="ghost"
            color="slate"
            @click="goToExecutions"
          >
            {{ t('FLOW_BUILDER.EDITOR.EXECUTIONS') }}
          </Button>
          <Button
            icon="i-lucide-bar-chart-3"
            size="sm"
            variant="ghost"
            color="slate"
            @click="goToReports"
          >
            {{ t('FLOW_BUILDER.EDITOR.REPORTS') }}
          </Button>
          <div class="w-px h-4 bg-n-strong" />
          <span class="text-xs text-n-slate-10 hidden sm:inline">
            {{
              isActive
                ? t('FLOW_BUILDER.COMMON.ACTIVE')
                : t('FLOW_BUILDER.COMMON.INACTIVE')
            }}
          </span>
          <Switch :model-value="isActive" @change="onToggleActive" />
          <div class="w-px h-4 bg-n-strong" />
          <Button
            icon="i-lucide-save"
            size="sm"
            :is-loading="isSaving"
            @click="onSave"
          >
            {{ t('FLOW_BUILDER.COMMON.SAVE') }}
          </Button>
        </div>
      </div>

      <!-- Editor layout -->
      <div class="flex flex-1 min-h-0 overflow-hidden">
        <!-- Canvas -->
        <div
          ref="canvasRef"
          class="flex-1 relative h-full"
          @drop="onDrop"
          @dragover="onDragOver"
        >
          <VueFlow
            v-model:nodes="nodes"
            v-model:edges="edges"
            :node-types="nodeTypes"
            :edge-types="edgeTypes"
            :default-viewport="{ zoom: 1 }"
            :min-zoom="0.3"
            :max-zoom="2"
            fit-view-on-init
            class="bg-n-surface-1"
            @node-click="onNodeClick"
            @node-context-menu="onNodeContextMenu"
            @pane-click="onPaneClick"
            @connect-start="onConnectStart"
            @connect-end="onConnectEnd"
          >
            <Background
              variant="dots"
              pattern-color="#94a3b8"
              :gap="16"
              :size="1.5"
            />
            <Controls />
            <MiniMap />
          </VueFlow>
        </div>
      </div>
      <!-- end editor layout -->
    </div>
    <!-- end main col -->

    <NodeConfigPanel
      :node="selectedNode"
      @update="onNodeDataUpdate"
      @close="selectedNode = null"
    />

    <NodePickerModal
      :show="picker.open"
      @select="onPickNode"
      @close="picker.open = false"
    />

    <Teleport to="body">
      <div
        v-if="contextMenu"
        v-on-click-outside="() => (contextMenu = null)"
        class="fixed z-[9999] bg-n-solid-1 border border-n-weak rounded-lg shadow-lg py-1 min-w-[140px]"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
        @click.stop
      >
        <button
          type="button"
          class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-ruby-11 hover:bg-n-alpha-2 transition-colors"
          @click="deleteNode(contextMenu.nodeId)"
        >
          <span class="i-lucide-trash-2 size-4" />
          {{ t('FLOW_BUILDER.COMMON.DELETE') }}
        </button>
      </div>
    </Teleport>
  </div>
</template>
